import { createFeatureSelector, createSelector } from '@ngrx/store';
import { PRACTISE_FEATURE_KEY, PractiseState } from './practise.reducer';
import {
  selectAvailableTypes,
  selectPersonScopesError,
  selectPersonScopesLoaded,
  selectPersonScopesLoading,
  selectScopeEntities,
} from '../scope/scope.selectors';
import {
  BookProgress,
  BookSwitchData,
  ExerciseWithResults,
  Paragraph,
  ParagraphWithProgress,
  PersonUsesBook,
  PractiseHomeCard,
  PractiseOverviewCard,
  Scope,
} from '@lyceo/lyceo-pupil-app/practise/util-models';
import {
  readablePractiseType,
  sortPractiseType,
} from '@lyceo/lyceo-pupil-app/practise/util';
import {
  selectAllPersonUsesBooks,
  selectLatestPersonUsesBooks,
} from '../person-uses-book/person-uses-book.selectors';
import {
  selectAllMethods,
  selectScopeMethodsError,
  selectScopeMethodsLoaded,
  selectScopeMethodsLoading,
} from '../method/method.selectors';
import {
  selectBookParamParagraphs,
  selectParagraphEntities,
} from '../paragraph/paragraph.selectors';
import {
  selectAllBookProgresses,
  selectTocBookProgressError,
  selectTocBookProgressLoading,
} from '../book-progress/book-progress.selectors';
import {
  selectAllBooks,
  selectParamFinalExamQuestionnaireId,
  selectTocBookError,
  selectTocBookLoading,
} from '../book/book.selectors';
import { selectAllParagraphProgresses } from '../paragraph-progress/paragraph-progress.selectors';
import { selectParamExercises } from '../exercise/exercise.selectors';
import { selectAllExaminationSessions } from '../examination-session/examination-session.selectors';
import { mapExaminationSessionToExerciseResult } from '../examination-session/examination-session.functions';
import { isDefined } from '@lyceo/shared/util';

export const selectPractiseState =
  createFeatureSelector<PractiseState>(PRACTISE_FEATURE_KEY);

export const selectScopeIds = createSelector(
  selectPractiseState,
  (state: PractiseState) => state.scopeIds,
);

export const selectSelectedPractiseType = createSelector(
  selectPractiseState,
  (state: PractiseState) => state.selectedPractiseType,
);

export const selectSegmentOptions = createSelector(
  selectAvailableTypes,
  (types) =>
    types.sort(sortPractiseType).map((type) => {
      return {
        value: type,
        label: readablePractiseType(type, types.length),
      };
    }),
);

export const selectScopesByIds = createSelector(
  selectScopeEntities,
  selectScopeIds,
  (scopes, scopeIds): Scope[] =>
    scopeIds.map((scopeId) => scopes[scopeId]).filter(isDefined),
);

export const selectScopesByPractiseType = createSelector(
  selectScopesByIds,
  selectSelectedPractiseType,
  (scopes, selectedPractiseType) =>
    scopes.filter((scope) => scope.type === selectedPractiseType),
);

export const selectPractiseOverviewCard = createSelector(
  selectScopesByPractiseType,
  selectAllPersonUsesBooks,
  selectParagraphEntities,
  selectAllMethods,
  selectAllBookProgresses,
  selectAllBooks,
  (
    scopes,
    personUsesBooks,
    paragraphs,
    methods,
    bookProgresses,
    books,
  ): PractiseOverviewCard[] =>
    scopes
      .map((scope) => {
        let optionalParagraph: Paragraph | undefined = undefined;
        let optionalBookProgress: BookProgress | undefined = undefined;
        const optionalPersonUsesBook: PersonUsesBook | undefined =
          personUsesBooks.find(
            (personUsesBook) => personUsesBook.scopeId === scope.id,
          );
        if (optionalPersonUsesBook) {
          optionalParagraph =
            paragraphs[optionalPersonUsesBook.lastUsedParagraphId];
          optionalBookProgress = bookProgresses.find(
            (bookProgress) =>
              bookProgress.bookId === optionalPersonUsesBook.bookId,
          );
        }
        return {
          scope: scope,
          personUsesBook: optionalPersonUsesBook,
          paragraph: optionalParagraph,
          bookProgress: optionalBookProgress,
          methods: methods.filter((method) =>
            method.scopeIds.includes(scope.id),
          ),
          books: books.filter(
            (book) => book.scopeIds && book.scopeIds.includes(scope.id),
          ),
        };
      })
      .filter((card) => card.methods.length),
);

export const selectPractiseOverviewLoaded = createSelector(
  selectPersonScopesLoaded,
  selectScopeMethodsLoaded,
  (personScopesLoaded, scopeMethodsLoaded) =>
    personScopesLoaded && scopeMethodsLoaded,
);

export const selectPractiseOverviewLoading = createSelector(
  selectPersonScopesLoading,
  selectScopeMethodsLoading,
  (personScopesLoading, scopeMethodsLoading) =>
    personScopesLoading || scopeMethodsLoading,
);

export const selectPractiseOverviewError = createSelector(
  selectPersonScopesError,
  selectScopeMethodsError,
  (personScopesError, scopeMethodsError) =>
    !!personScopesError || !!scopeMethodsError,
);

export const selectPractiseHomeCard = createSelector(
  selectLatestPersonUsesBooks,
  selectScopeEntities,
  selectParagraphEntities,
  selectAllBookProgresses,
  (personUsesBooks, scopes, paragraphs, bookProgresses): PractiseHomeCard[] =>
    personUsesBooks.map((personUsesBook) => {
      const currentScope: Scope | undefined = scopes[personUsesBook.scopeId];
      return {
        personUsesBook,
        scope: currentScope,
        paragraph: paragraphs[personUsesBook.lastUsedParagraphId],
        bookProgress: bookProgresses.find(
          (bookProgress) => bookProgress.bookId === personUsesBook.bookId,
        ),
      };
    }),
);

export const selectParagraphsWithProgress = createSelector(
  selectBookParamParagraphs,
  selectAllParagraphProgresses,
  (paragraphs, paragraphProgresses): ParagraphWithProgress[] =>
    paragraphs.map((paragraph) => {
      return {
        paragraph,
        paragraphProgress: paragraphProgresses.find(
          (paragraphProgress) => paragraphProgress.paragraphId === paragraph.id,
        ),
      };
    }),
);

export const selectBookTocLoading = createSelector(
  selectTocBookLoading,
  selectTocBookProgressLoading,
  (tocBookLoading, tocBookProgressLoading) =>
    tocBookLoading || tocBookProgressLoading,
);

export const selectBookTocError = createSelector(
  selectTocBookError,
  selectTocBookProgressError,
  (tocBookError, tocBookProgressError) =>
    !!tocBookError || !!tocBookProgressError,
);

export const selectExercisesWithResults = createSelector(
  selectParamExercises,
  selectAllExaminationSessions,
  (exercises, examinationSessions): ExerciseWithResults[] =>
    exercises.map((exercise) => {
      return {
        exercise,
        results: examinationSessions
          .filter(
            (examinationSession) =>
              examinationSession.questionnaireId === exercise.questionnaireId,
          )
          .map((examinationSession) =>
            mapExaminationSessionToExerciseResult(examinationSession),
          )
          .slice(0, 3),
      };
    }),
);

export const selectFinalExamResults = createSelector(
  selectAllExaminationSessions,
  selectParamFinalExamQuestionnaireId,
  (examinationSessions, questionnaireId) =>
    examinationSessions
      .filter(
        (examinationSession) =>
          examinationSession.questionnaireId === questionnaireId,
      )
      .map((examinationSession) =>
        mapExaminationSessionToExerciseResult(examinationSession),
      ),
);

export const selectScopeIdsWithoutPersonUsesBook = createSelector(
  selectScopeIds,
  selectAllPersonUsesBooks,
  (scopeIds, personUsesBooks) =>
    scopeIds.filter((scopeId) =>
      personUsesBooks.every(
        (personUsesBook) => personUsesBook.scopeId !== scopeId,
      ),
    ),
);

export const selectSingleBookSwitches = createSelector(
  selectScopeIdsWithoutPersonUsesBook,
  selectAllBooks,
  (scopeIds, books) =>
    scopeIds.reduce((results: BookSwitchData[], scopeId) => {
      const filteredBooks = books.filter(
        (book) => book.scopeIds && book.scopeIds.includes(scopeId),
      );

      if (filteredBooks.length === 1) {
        results.push({
          scopeId,
          bookId: filteredBooks[0].id,
        });
      }

      return results;
    }, []),
);
